import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Link,
  Chip,
  Collapse,
  IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useLocation } from 'react-router-dom';
import FortnoxInvoiceTable from './FortnoxInvoiceTable';

const FortnoxIntegration = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [status, setStatus] = useState({ connected: false });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const location = useLocation();
  const [missingLicense, setMissingLicense] = useState(false);

  useEffect(() => {
    // Check for URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const hasSuccess = urlParams.has('fortnox_success');
    const hasError = urlParams.has('fortnox_error');
    const hasMissingLicense = urlParams.has('fortnox_missing_license');

    if (hasMissingLicense) {
      setMissingLicense(true);
    } else if (hasError) {
      setError(urlParams.get('fortnox_error'));
    } else if (hasSuccess) {
      setSuccess('Fortnox-anslutningen har slutförts. Du är nu ansluten till Fortnox.');
    }

    // Clean URL parameters
    if (hasSuccess || hasError || hasMissingLicense) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    checkConnectionStatus();
  }, [user]);

  // When connection status changes to connected, collapse the card for a cleaner interface
  useEffect(() => {
    if (status.connected && !tokenExpired && !loading) {
      setExpanded(false);
    }
  }, [status.connected, tokenExpired, loading]);

  const checkConnectionStatus = async () => {
    try {
      setLoading(true);
      setTokenExpired(false); // Reset token expired state
      
      const response = await api.get('/api/fortnox/status');
      
      // Handle specific error codes
      if (!response.data.connected && response.data.error_code === 'token_expired') {
        setTokenExpired(true);
      }
      
      setStatus(response.data);
    } catch (err) {
      console.error('Error checking Fortnox connection status:', err);
      setError('Det gick inte att kontrollera Fortnox-anslutningen. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    setTokenExpired(false);
    console.log('Starting Fortnox connection process...');
    try {
      // Use the API service which will include the authentication token
      console.log('Making request to /api/fortnox/authorize...');
      const response = await api.get('/api/fortnox/authorize');
      console.log('Response received:', response);
      
      // Check if we got a direct response with a redirect URL
      if (response.data && response.data.redirect_url) {
        console.log('Received redirect URL:', response.data.redirect_url);
        // Add a small delay to ensure logs are visible in console
        setTimeout(() => {
          console.log('Redirecting to Fortnox...');
          window.location.href = response.data.redirect_url;
        }, 100);
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Oväntad respons från servern. Försök igen senare.');
      }
    } catch (err) {
      console.error('Error connecting to Fortnox:', err);
      if (err.response) {
        console.error('Error details:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
          headers: err.response.headers
        });
      }
      setError('Det gick inte att ansluta till Fortnox. Försök igen senare.');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await api.get('/api/fortnox/disconnect');
      setStatus({ connected: false });
      setSuccess('Fortnox-anslutningen har kopplats från.');
      setExpanded(true);
    } catch (err) {
      console.error('Error disconnecting from Fortnox:', err);
      setError('Det gick inte att koppla från Fortnox. Försök igen senare.');
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <>
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ padding: status.connected && !tokenExpired ? '16px 16px 8px 16px' : undefined }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: status.connected && !tokenExpired && !expanded ? 0 : 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" component="h2">
                Fortnox-Integration
              </Typography>
              {status.connected && (
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label="Ansluten" 
                  color="success" 
                  size="small" 
                  sx={{ ml: 2 }} 
                />
              )}
              {tokenExpired && (
                <Chip 
                  icon={<ErrorOutlineIcon />} 
                  label="Anslutning utgången" 
                  color="error" 
                  size="small" 
                  sx={{ ml: 2 }} 
                />
              )}
            </Box>
            
            {status.connected && !tokenExpired && (
              <IconButton 
                onClick={handleToggleExpand} 
                size="small"
                aria-expanded={expanded}
                aria-label={expanded ? "visa mindre" : "visa mer"}
              >
                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </Box>
          
          {(expanded || !status.connected || tokenExpired) && <Divider sx={{ mb: 2 }} />}
          
          <Collapse in={expanded || !status.connected || tokenExpired}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {tokenExpired && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Din anslutning till Fortnox har gått ut
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Fortnox OAuth-tokens gäller i 45 dagar. Du behöver ansluta på nytt för att fortsätta använda integrationen.
                    </Typography>
                  </Alert>
                )}
                
                {missingLicense && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography>
                      Du behöver skaffa integrationslicens hos Fortnox för att ansluta ditt konto.
                    </Typography>
                    <Link 
                      href="https://checkout.fortnox.se/program/15" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      Klicka här för att skaffa licens
                    </Link>
                  </Alert>
                )}

                {error && !missingLicense && !tokenExpired && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}
                
                {success && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                  </Alert>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Anslut ditt konto till Fortnox för att kunna skicka recensionsförfrågningar till dina fakturerade kunder.
                  </Typography>

                  {status.connected && !tokenExpired ? (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          Ditt konto är anslutet till Fortnox med följande behörigheter:
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {status.scopes && status.scopes.map(scope => (
                            <Chip 
                              key={scope} 
                              label={scope} 
                              size="small" 
                              color="primary" 
                              variant="outlined" 
                            />
                          ))}
                        </Box>
                      </Alert>

                      <Button 
                        variant="outlined" 
                        color="error"
                        onClick={handleDisconnect}
                        disabled={disconnecting}
                        startIcon={disconnecting ? <CircularProgress size={20} /> : <WarningIcon />}
                      >
                        {disconnecting ? 'Kopplar från...' : 'Koppla från Fortnox'}
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Alert severity="info" icon={<InfoIcon />}>
                        Genom att ansluta till Fortnox ger du appen behörighet att komma åt kund- och fakturainformation.
                        {tokenExpired && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              Observera att enligt Fortnox säkerhetspolicy behöver du förnya din anslutning var 45:e dag.
                            </Typography>
                          </Box>
                        )}
                      </Alert>
                      
                      <Button 
                        variant="contained" 
                        color="primary"
                        onClick={handleConnect}
                        disabled={connecting}
                        startIcon={connecting ? <CircularProgress size={20} color="inherit" /> : null}
                      >
                        {connecting ? 'Ansluter...' : (tokenExpired ? 'Förnya anslutning till Fortnox' : 'Anslut till Fortnox')}
                      </Button>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Collapse>
        </CardContent>
      </Card>

      {/* Only show the invoice table if connected to Fortnox */}
      {status.connected && !tokenExpired && (
        <FortnoxInvoiceTable />
      )}
    </>
  );
};

export default FortnoxIntegration; 